import type { Home } from '@lmc/ts-common'
import { PageHeader, Spinner } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Tabs } from 'antd'
import useResident from 'hooks/useResident'
import useResidentFullName from 'hooks/useResidentFullName'
import { NAMESPACE } from 'locale'
import { emarV2Constants } from 'locale/emarV2'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { EMARInitialAssessmentEmptyState } from '../../ResidentEmarScreens/components'
import { AddMedicationButton } from '../componentsV2/AddMedicationButton'
import EMARScreenEmptyStateV2 from '../componentsV2/EMARScreenEmptyStateV2'
import PageWrapper from '../componentsV2/PageWrapper'
import { useResidentMedications } from '../hooks/useResidentMedications'
import type { ResidentMedication } from '../types'
import { StockSection } from './StockSection'

const EMARResidentStockScreenV2 = () => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const { id: residentId } = useParams<{ id: string }>()
  const residentName = useResidentFullName(residentId)
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const { resident, isLoading: isLoadingResident } = useResident({
    id: residentId
  })
  const [activeTab, setActiveTab] = useState<string>('active')

  const {
    data: medicationsData,
    isLoading: isLoadingMedications,
    refetch
  } = useResidentMedications({
    homeId: home?.id || '',
    residentId: residentId || ''
  })

  const isModalOpen = useSelector(
    (state: { modals: { addEmarMedication?: { isOpen?: boolean } } }) =>
      !!state.modals.addEmarMedication?.isOpen
  )
  const prevModalOpenRef = useRef<boolean>(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (prevModalOpenRef.current === true && isModalOpen === false) {
      refetch()
    }
    prevModalOpenRef.current = isModalOpen
  }, [isModalOpen, refetch])

  const isLoading = isLoadingResident || isLoadingMedications

  const isInitialAssessment =
    resident?.status === 'pre-assessment' || resident?.isActiveInitialAssessment

  const sortByName = (a: ResidentMedication, b: ResidentMedication) =>
    a.medicationName.localeCompare(b.medicationName)
  const scheduledMedications = [
    ...(medicationsData?.scheduledMedications || [])
  ].sort(sortByName)
  const prnMedications = [...(medicationsData?.prnMedications || [])].sort(
    sortByName
  )
  const discontinuedMedications = [
    ...(medicationsData?.discontinuedMedications || [])
  ].sort(sortByName)

  const activeMedications = [...scheduledMedications, ...prnMedications]

  const waitingForDisposalMedications = discontinuedMedications.filter(
    (med) => (med.currentStockLevel ?? 0) > 0
  )
  const noStockLeftMedications = discontinuedMedications.filter(
    (med) => (med.currentStockLevel ?? 0) === 0
  )

  const handleTabChange = (key: string) => {
    setActiveTab(key)
  }

  if (isLoading || !resident) {
    return (
      <PageWrapper>
        <Spinner style={{ marginTop: '80px' }} />
      </PageWrapper>
    )
  }

  const tabItems = [
    {
      key: 'active',
      label: <Text>{t('ACTIVE')}</Text>,
      children: (
        <TabContent>
          {activeMedications.length === 0 ? (
            <EMARScreenEmptyStateV2 resident={resident} type="active" />
          ) : (
            <>
              {scheduledMedications.length > 0 && (
                <StockSection
                  title={t('SCHEDULED')}
                  medications={scheduledMedications}
                  residentId={residentId || ''}
                />
              )}
              {prnMedications.length > 0 && (
                <StockSection
                  title={t('NOT_SCHEDULED_PRN')}
                  medications={prnMedications}
                  residentId={residentId || ''}
                />
              )}
            </>
          )}
        </TabContent>
      )
    },
    {
      key: 'discontinued',
      label: <Text>{t('DISCONTINUED')}</Text>,
      children: (
        <TabContent>
          {discontinuedMedications.length === 0 ? (
            <EMARScreenEmptyStateV2
              resident={resident}
              type="discontinued"
              discontinuedFromDate={new Date()}
            />
          ) : (
            <>
              {waitingForDisposalMedications.length > 0 && (
                <StockSection
                  title={t('WAITING_FOR_DISPOSAL')}
                  medications={waitingForDisposalMedications}
                  residentId={residentId || ''}
                />
              )}
              {noStockLeftMedications.length > 0 && (
                <StockSection
                  title={t('NO_STOCK_LEFT')}
                  medications={noStockLeftMedications}
                  residentId={residentId || ''}
                />
              )}
            </>
          )}
        </TabContent>
      )
    }
  ]

  return (
    <>
      <PageHeader
        title={
          residentName
            ? t(emarV2Constants.MEDICATION_STOCK_FOR, { name: residentName })
            : t(emarV2Constants.MEDICATION_STOCK)
        }
        sticky
      >
        <AddMedicationButton resident={resident} />
      </PageHeader>
      <PageWrapper>
        {isInitialAssessment ? (
          <EMARInitialAssessmentEmptyState
            preferredPeopleNominative={
              home?.preferredPeopleNominative || 'person'
            }
            resident={resident}
          />
        ) : (
          <StyledTabs
            defaultActiveKey="active"
            activeKey={activeTab}
            items={tabItems}
            onChange={handleTabChange}
          />
        )}
      </PageWrapper>
    </>
  )
}

const StyledTabs = styled(Tabs)`
  width: 100%;
  .ant-tabs-nav {
    margin: 0 0 0 24px;
  }
`

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
`

export default EMARResidentStockScreenV2
